import { OnInit, OnDestroy } from '@angular/core';
import { Component, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { Router} from '@angular/router';
import { RouterOutlet, NavigationEnd } from '@angular/router';
import { HeadersComponent } from './components/headers/headers.component';
import { WelcomeComponent } from './components/welcome/welcome.component';
import { ProfileSelectorComponent } from './components/profile-selector/profile-selector.component';
import { ToastOutletComponent } from './components/toast-outlet/toast-outlet.component';
import { RdsModule } from '@rabobank/rds-angular';
import { CommonModule } from '@angular/common';
import { Subject, filter, takeUntil } from 'rxjs';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { BatchStateService } from './services/batch-state.service';
import { EventMessage} from '@azure/msal-browser';
import { EventType } from '@azure/msal-browser';

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, HeadersComponent, WelcomeComponent, ProfileSelectorComponent, RdsModule, ToastOutletComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
   schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Ccuberds';
  isOnBatchRoute = false;
  isVerifyRoute = false;
  private destroy$ = new Subject<void>();
  private popStateGlobalHandler: ((e: PopStateEvent) => void) | null = null;
  private pageShowGlobalHandler: ((e: PageTransitionEvent) => void) | null = null;

  constructor(
    private router: Router,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private batchStateService: BatchStateService
  ) {}

  public get showHeader(): boolean {
    return this.isOnBatchRoute && !this.isVerifyRoute;
  }

  public ngOnInit(): void {
    this.syncRouteFlags(this.router.url || window.location.pathname || '');

    // Initialize MSAL and handle redirect
    this.msalService.instance.initialize().then(() => {
      this.msalService.instance.handleRedirectPromise().then((response) => {
        if (response) {
          console.log('Authentication response received:', response);
          this.msalService.instance.setActiveAccount(response.account);
        } else {
          // Check if there's already an account
          const accounts = this.msalService.instance.getAllAccounts();
          if (accounts.length > 0) {
            console.log('Using existing account:', accounts[0]);
            this.msalService.instance.setActiveAccount(accounts[0]);
          }
        }
      }).catch((error) => {
        console.error('Error handling redirect:', error);
      });
    });

    // Listen to MSAL events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
        takeUntil(this.destroy$)
      )
      .subscribe((result: EventMessage) => {
        console.log('MSAL Event:', result);
      });

    // Listen to route changes to hide welcome screen when on batch routes and to detect verify route
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event: NavigationEnd) => {
      this.syncRouteFlags(event.urlAfterRedirects || event.url);
    });

    // Evaluate current URL once on init in case app was loaded with auth redirect params
    this.updateAuthBodyClass();

    // Global handlers to clear batch list state when navigating via browser
    // forward/back to the batch list so filters/pagination are reset.
    try {
      if (typeof window !== 'undefined' && window && window.addEventListener) {
        this.popStateGlobalHandler = (ev: PopStateEvent) => {
          try {
            const url = (window.location.pathname || '').toLowerCase();
            if (url.includes('/batches')) {
              this.batchStateService.clearState();
            }
          } catch (e) { /* ignore */ }
        };
        this.pageShowGlobalHandler = (_ev: PageTransitionEvent) => {
          try {
            const url = (window.location.pathname || '').toLowerCase();
            if (url.includes('/batches')) {
              this.batchStateService.clearState();
            }
          } catch (e) { /* ignore */ }
        };
        window.addEventListener('popstate', this.popStateGlobalHandler);
        window.addEventListener('pageshow', this.pageShowGlobalHandler);
      }
    } catch (e) {
      // ignore
    }
  }

  // If the current url contains auth redirect tokens/params (MSAL redirect flow)
  // we suppress the page-level scrollbar to avoid a double-scroll experience
  // while the provider's UI / transient redirect page is visible.
  private syncRouteFlags(url: string): void {
    const normalizedUrl = (url || '').toLowerCase();
    this.isOnBatchRoute = normalizedUrl.includes('/batches');
    this.isVerifyRoute = normalizedUrl.includes('/verify');
    this.updateAuthBodyClass();
  }

  private updateAuthBodyClass(): void {
    try {
      // Hide scroll when the app is showing the authorization/welcome
      // screen (the `main-container center-wrapper` block). That view
      // is shown when `isOnBatchRoute` is false — use that boolean so we
      // don't rely solely on URL tokens. Also keep previous behaviour
      // that hides scroll for auth redirect params.
      const url = window.location.href || '';
      const hasAuthParams = url.includes('code=') || url.includes('id_token=') || url.includes('error=') || url.includes('session_state=') || url.includes('state=');
      const shouldHide = (!this.isOnBatchRoute) || hasAuthParams;
      if (shouldHide) {
        document.body.classList.add('no-scroll-auth');
        document.documentElement.classList.add('no-scroll-auth');
      } else {
        document.body.classList.remove('no-scroll-auth');
        document.documentElement.classList.remove('no-scroll-auth');
      }
    } catch (e) {
      // Ignore in environments without DOM
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    try {
      if (this.popStateGlobalHandler) { window.removeEventListener('popstate', this.popStateGlobalHandler); this.popStateGlobalHandler = null; }
    } catch (e) {}
    try {
      if (this.pageShowGlobalHandler) { window.removeEventListener('pageshow', this.pageShowGlobalHandler); this.pageShowGlobalHandler = null; }
    } catch (e) {}
  }
}
